import { useCallback, useEffect, useMemo, useState } from 'react';

import { NOVIDADES, PublicoNovidade, VersaoNovidades } from '../data/novidades';
import useAuth from './useAuth';

const PREFIXO = '@novidades';

/**
 * O "ja li" fica preso ao usuario logado (`@fId`, como as anotacoes do material),
 * senao a leitura de um professor esconderia o mural do proximo que entrasse no
 * computador da secretaria.
 */
function chaveArmazenamento(functionId: string) {
	return functionId ? `${PREFIXO}:${functionId}` : PREFIXO;
}

function lerUltimaLida(functionId: string) {
	try {
		return localStorage.getItem(chaveArmazenamento(functionId)) ?? '';
	} catch (erro) {
		// Storage bloqueado (aba anonima): o mural funciona, so sempre com o aviso de novidade.
		return '';
	}
}

// O aviso do menu e a pagina de novidades sao dois usos do hook vivendo em pontos
// diferentes da arvore. Sem esta notificacao, abrir a pagina marcaria tudo como
// lido mas o contador do menu so sumiria na proxima navegacao.
const ouvintes = new Set<() => void>();

/** Esconde os itens que nao sao do perfil e descarta a versao que ficar sem nenhum item. */
function filtrarPorPerfil(versoes: VersaoNovidades[], perfil: PublicoNovidade) {
	return versoes
		.map((versao) => ({
			...versao,
			itens: versao.itens.filter((item) => !item.publico || item.publico.includes(perfil))
		}))
		.filter((versao) => versao.itens.length > 0);
}

/**
 * Alimenta o mural de novidades: a lista ja filtrada pelo perfil de quem esta
 * logado e quantas entregas essa pessoa ainda nao abriu.
 */
export function useNovidades() {
	const { user, functionId } = useAuth();
	const [ ultimaLida, setUltimaLida ] = useState<string>('');

	const perfil: PublicoNovidade = user?.userType === 'ADMIN' ? 'ADMIN' : 'PROFESSOR';

	const versoes = useMemo(() => filtrarPorPerfil(NOVIDADES, perfil), [ perfil ]);

	// O `functionId` chega depois do primeiro render, quando o AuthContext termina
	// de resolver a sessao, entao a leitura precisa acompanha-lo.
	useEffect(
		() => {
			const sincronizar = () => setUltimaLida(lerUltimaLida(functionId));

			sincronizar();
			ouvintes.add(sincronizar);

			return () => {
				ouvintes.delete(sincronizar);
			};
		},
		[ functionId ]
	);

	const naoLidas = useMemo(() => versoes.filter((versao) => versao.data > ultimaLida).length, [ versoes, ultimaLida ]);

	const marcarComoLidas = useCallback(
		() => {
			const maisRecente = versoes[0]?.data;
			if (!maisRecente) return;

			try {
				localStorage.setItem(chaveArmazenamento(functionId), maisRecente);
			} catch (erro) {
				// Sem storage o aviso reaparece na proxima visita, o que e melhor que quebrar a tela.
			}

			ouvintes.forEach((ouvinte) => ouvinte());
		},
		[ versoes, functionId ]
	);

	return { versoes, naoLidas, marcarComoLidas };
}
